import { t } from 'i18next';
import { Candidate } from 'models/Candidate';
import { ProfileCardType } from 'models/ProfileCard.type';
import { User } from 'models/User';
import { Vacancy } from 'models/Vacancy';
import { JobApplicationFormValues } from 'router/subrouters/JobApplication/types/JobApplicationFormValues';
import {
  CurriculumExperience,
  RightToWorkType,
  YearsOfExperience,
} from 'searchality-data';
import { TeacherFormValues } from '../models/TeacherFormValues';
import TeacherProfileDataService from '../services/TeacherProfileData.service';

export function extractProfileInfoFromUser(user: User) {
  const {
    _id,
    firstName,
    lastName,
    email,
    hasRightToWork,
    systemColor,
    startDate,
    educationPhilosophyUrl,
    videoUrl,
    rightToWork,
    education,
    experience,
    curricula,
    sports,
    activities,
    languages,
    englishLevel,
    educationPhilosophyKeyUpdatedAt,
    references,
    imageFile,
    certificates,
    profilePdfUrl,
    publications,
    visaType,
    rightToWorkDescription,
    profileStatus,
  } = user;
  return {
    _id,
    firstName,
    lastName,
    email,
    profilePdfUrl,
    systemColor,
    startDate,
    hasRightToWork,
    educationPhilosophyUrl,
    educationPhilosophyKeyUpdatedAt,
    videoUrl,
    rightToWork,
    education,
    experience,
    curricula,
    sports,
    activities,
    languages,
    englishLevel,
    references,
    certificates,
    visaType,
    publications,
    rightToWorkDescription,
    profileStatus,
    teacherId: _id,
    imageUrl: imageFile?.url,
  } as unknown as ProfileCardType;
}

export function extractProfileInfoFromJobApplication(
  values: JobApplicationFormValues,
  candidate?: User,
  vacancy?: Vacancy,
) {
  if (JSON.stringify(values) === '{}') return;

  const {
    startDate: jobStartingDate,
    curriculumExperience: vacancyCurricullumExperience,
    educationLevel,
    yearsOfExperienceRequired,
  } = vacancy;

  const {
    personalInformation: {
      firstName,
      lastName,
      email,
      phoneNumber: mobilePhone,
      address,
      videoUrl,
      applicantPhoto,
    } = {},

    yourProfile: {
      education,
      experience,
      educationPhilosophy,
      coverLetter,
      resume,
    } = {},
    positionRequirements: {
      languageRequirement: languages,
      rightToWork,
      visaType,
      dateUntilVisaIsValid,
      rightToWorkDescription,
      isAbleToStartOnRequiredDate,
      earliestAvailableStartingDate,
      hasRightToWork,
      yearsOfExperienceWithFaithBasedSchools,
      doesHaveExperinceWithFaithBasedSchools,
      doesHaveExperienceInTheField,
      yearsOfExperienceInTheField,
      englishLevel,
      doesMeetMinimumEducationLevel,
      doesHaveYearsOfExperience,
    } = {},
    references,
    publications,
  } = values || ({} as JobApplicationFormValues);

  const cardInfo: ProfileCardType = {
    firstName,
    lastName,
    email,
    mobilePhone,
    address,
    videoUrl,
    englishLevel,
    education,
    yearsOfExperienceWithFaithBasedSchools,
    doesHaveExperinceWithFaithBasedSchools,
    doesHaveYearsOfExperience,
    doesMeetMinimumEducationLevel,
    ...(doesMeetMinimumEducationLevel && { educationLevel }),
    ...(yearsOfExperienceRequired && { yearsOfExperienceRequired }),
    references,
    experience,
    resumeUrl: resume?.length ? URL.createObjectURL(resume[0]) : undefined,
    educationPhilosophyUrl: educationPhilosophy?.length
      ? URL.createObjectURL(educationPhilosophy[0])
      : undefined,
    coverLetterUrl: coverLetter?.length
      ? URL.createObjectURL(coverLetter[0])
      : undefined,
    languages: Object.keys(languages || {}).map((key) => ({
      language: key,
      level: languages[key].level ? languages[key].level : undefined,
      doesSpeak: languages[key].level ? true : false,
    })),
    rightToWork: [rightToWork],
    visaType,
    rightToWorkDescription,
    dateUntilVisaIsValid,
    imageUrl: applicantPhoto?.length
      ? URL.createObjectURL(applicantPhoto[0])
      : undefined,
    systemColor: '',
    startDate: isAbleToStartOnRequiredDate
      ? jobStartingDate ?? null
      : earliestAvailableStartingDate,
    hasRightToWork,
    sports: candidate?.sports ?? undefined,
    activities: candidate?.activities ?? undefined,
    curricula: doesHaveExperienceInTheField
      ? [
          {
            yearsOfExperience: yearsOfExperienceInTheField,
            curriculumExperience:
              vacancyCurricullumExperience as CurriculumExperience,
          },
        ]
      : undefined,
    certificates: candidate?.certificates,
    publications:
      TeacherProfileDataService.formatPublicationFormIntoPublications(
        publications,
      ),
  };

  return cardInfo;
}

export function extractProfileInfoFromCandidate(candidate: Candidate) {
  if (JSON.stringify(candidate) === '{}') return;

  const { vacancy } = candidate || {};

  const {
    startDate,
    curriculumExperience: vacancyCurricula,
    educationLevel,
    yearsOfExperienceRequired,
  } = vacancy || {};

  const {
    _id,
    firstName,
    lastName,
    email,
    educationPhilosophyUrl,
    educationPhilosophyKeyUpdatedAt,
    coverLetterKeyUpdatedAt,
    resumeKeyUpdatedAt,
    coverLetterUrl,
    videoUrl,
    resumeUrl,
    education,
    type,
    earliestAvailableStartingDate,
    isAbleToStartOnRequiredDate,
    experience,
    address,
    references,
    yearsOfExperienceInTheField,
    doesHaveExperienceInTheField,
    doesHaveExperinceWithFaithBasedSchools,
    yearsOfExperienceWithFaithBasedSchools,
    doesHaveYearsOfExperience,
    doesMeetMinimumEducationLevel,
    imageFile,
    rightToWork,
    color,
    updatedAt,
    isFavourite,
    hasRightToWork,
    rightToWorkDescription,
    teacher,
    englishLevel,
    publications,
    languageRequirement,
  } = candidate;

  const cardInfo: ProfileCardType = {
    firstName,
    _id,
    lastName,
    email,
    videoUrl,
    resumeUrl,
    educationPhilosophyKeyUpdatedAt,
    resumeKeyUpdatedAt,
    coverLetterKeyUpdatedAt,
    doesHaveYearsOfExperience,
    doesMeetMinimumEducationLevel,
    type,
    education,
    references,
    yearsOfExperienceWithFaithBasedSchools,
    doesHaveExperinceWithFaithBasedSchools,
    experience,
    educationPhilosophyUrl,
    ...(doesMeetMinimumEducationLevel && { educationLevel }),
    ...(yearsOfExperienceRequired && { yearsOfExperienceRequired }),
    coverLetterUrl,
    imageUrl: imageFile?.url,
    address,
    systemColor: color,
    startDate: isAbleToStartOnRequiredDate
      ? startDate
      : earliestAvailableStartingDate,
    rightToWork: rightToWork as RightToWorkType[],
    updatedAt,
    isFavourite,
    curricula: vacancyCurricula
      ? ([
          {
            yearsOfExperience: doesHaveExperienceInTheField
              ? (yearsOfExperienceInTheField?.toString() as YearsOfExperience)
              : (t('haveNoExperience') as YearsOfExperience),
            curriculumExperience: vacancyCurricula,
          },
        ] as any)
      : [],
    hasRightToWork,
    rightToWorkDescription,
    englishLevel,
    languages: languageRequirement,
    sports: teacher?.sports,
    activities: teacher?.activities,
    certificates: teacher?.certificates,
    publications,
  };

  return cardInfo;
}

export const convertTeacherFormValuesIntoCard = (
  formValues: User & TeacherFormValues,
) => {
  const {
    videoUrl,
    streetAddress,
    rightToWork,
    startDate,
    educationPhilosophyUrl,
    education,
    experience,
    systemColor,
    firstName,
    lastName,
    email,
    references,
    curricula,
    sports,
    publications,
    activities,
    mobilePhone,
    immediately,
    englishLevel,
    languages,
    dateUntilVisaIsValid,
    visaType,
    rightToWorkDescription,
    hasRightToWork,
    imageFile,
    certificates,
  } = formValues;

  const submitObject: ProfileCardType = {
    address: streetAddress,
    firstName,
    lastName,
    systemColor,
    email,
    startDate,
    educationPhilosophyUrl,
    videoUrl,
    references,
    education,
    experience,
    sports,
    activities,
    curricula,
    englishLevel,
    languages,
    mobilePhone,
    rightToWorkDescription,
    dateUntilVisaIsValid,
    visaType,
    rightToWork: [rightToWork] as unknown as RightToWorkType[],
    hasRightToWork,
    imageUrl: '',
    certificates,
  };

  if (publications) {
    submitObject.publications =
      TeacherProfileDataService.formatPublicationFormIntoPublications(
        publications,
      );
  }

  if (imageFile?.[0]) {
    submitObject.imageUrl = URL.createObjectURL(imageFile?.[0]);
  }

  if (immediately) {
    submitObject.startDate = null;
  } else {
    if (immediately === undefined) {
      submitObject.startDate = undefined;
    } else {
      submitObject.startDate = startDate;
    }
  }
  return submitObject;
};
